import { useCallback, useMemo } from 'react';
import { routes } from '@platform/core/lib/routes';
import type { IModuleDefinition, IModuleStrings, IRecordTypeDefinition, IStatBucket } from '@platform/core/types';
import { presetItemKey } from '@platform/engine/menu/adHocEntry';
import { buildMenu } from '@platform/engine/menu/buildMenu';
import type { IPresetLink } from './IPresetLink';

const NO_BUCKETS: IStatBucket[] = [];

/**
 * Посилання з тайлів і порад сторінок модуля на пункти лівого меню головного типу: пресет (`preset:<ключ>`) або «Усі …» (`view:table`,
 * інакше `all`) — той самий список, що відкриває меню, з його поданням. Пресета, якого меню не має, — пункт поза меню з тим самим ключем
 * (прототип v4: «Показати без виконавця» відкриває список і без такого пункту в меню завдань).
 */
export function usePresetLink(module: IModuleDefinition, rt: IRecordTypeDefinition, strings: IModuleStrings, buckets?: IStatBucket[]): IPresetLink {
  const moduleName = strings.moduleName(module.module.name);
  const entries = useMemo(
    () => buildMenu(rt, { status: buckets ?? NO_BUCKETS }, strings, moduleName).flatMap((s) => s.entries), [rt, buckets, strings, moduleName],
  );
  return useCallback((preset) => {
    if (preset) {
      const key = presetItemKey(preset);
      const entry = entries.find((e) => e.key === key);
      return routes.list(module.module.key, rt.key, entry?.view ?? undefined, key);
    }
    const entry = entries.find((e) => e.key === 'view:table') ?? entries.find((e) => e.key === 'all');
    return entry ? routes.list(module.module.key, rt.key, entry.view ?? undefined, entry.key) : undefined;
  }, [entries, module.module.key, rt.key]);
}
